import {
  VStack,
  Alert,
  AlertTitle,
  AlertDescription,
  AlertIcon,
  AlertProps,
  forwardRef,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { ButtonVariant } from "../../../components/atoms/ButtonVariant";
import { errorToastOptions } from "../../../components/molecules/errorToastOptions";

interface PlanAlertProps extends Omit<AlertProps, "status"> {
  title: string;
  description: React.ReactNode;
  action?: {
    label: string;
    onClickAction: () => Promise<void>;
    isDisabled: boolean;
  };
}

export const PlanAlert = forwardRef<PlanAlertProps, "div">(
  ({ action, description, title, ...alertProps }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast(errorToastOptions);

    const handleActionClick = async () => {
      if (!action?.onClickAction || isLoading) {
        return;
      }

      try {
        setIsLoading(true);
        await action.onClickAction();
      } catch (error) {
        toast({
          title: "Action failed",
          description: `Please try again later.`,
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Alert
        ref={ref}
        status="warning"
        variant="left-accent"
        alignItems="start"
        {...alertProps}
      >
        <AlertIcon />
        <VStack alignItems="flex-start">
          <AlertTitle>{title}</AlertTitle>
          <AlertDescription>{description}</AlertDescription>

          {action && (
            <ButtonVariant
              content={action.label}
              size="xs"
              outlineType="outlineBlack"
              isDisabled={action.isDisabled || isLoading}
              isLoading={isLoading}
              onClick={handleActionClick}
            />
          )}
        </VStack>
      </Alert>
    );
  },
);
